import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { IMG, BRAND } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { Play, Youtube, Headphones, Clock, ArrowRight } from "lucide-react";

const resolveImg = (img) => (img && img.startsWith("http") ? img : (IMG[img] || img));

const FeaturedEpisode = ({ ep }) => {
  if (!ep) return null;
  return (
    <Reveal>
      <article
        data-testid="podcast-featured"
        className="glass-strong lift overflow-hidden grid lg:grid-cols-12"
      >
        <div className="lg:col-span-6 relative aspect-[4/3] lg:aspect-auto overflow-hidden">
          <img
            src={resolveImg(ep.image)}
            alt={ep.title}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/40 to-transparent" />
          <div className="absolute bottom-6 left-6 flex items-center gap-3">
            <span className="font-display text-7xl text-gold-grad leading-none">
              {ep.number}
            </span>
            <div className="text-xs uppercase tracking-[0.3em] text-ivory/70">
              Latest<br />Episode
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 p-10 flex flex-col justify-center">
          <div className="text-xs uppercase tracking-[0.25em] text-gold">
            {ep.tag} · {ep.date} · {ep.duration}
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-ivory">
            {ep.title}
          </h2>
          <p className="mt-4 text-ivory/70 leading-relaxed">{ep.description}</p>
          <p className="mt-4 text-sm text-ivory/55">
            <span className="text-gold">Host:</span> {ep.host} ·{" "}
            <span className="text-gold">Guest:</span> {ep.guest}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={ep.listenUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
              data-testid={`podcast-listen-${ep.id}`}
            >
              <Play className="h-4 w-4" /> Listen Now
            </a>
            <a
              href={ep.youtubeUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost"
              data-testid={`podcast-youtube-${ep.id}`}
            >
              <Youtube className="h-4 w-4" /> Watch on YouTube
            </a>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

const EpisodeCard = ({ ep, i }) => (
  <Reveal delay={i * 60}>
    <article className="glass lift overflow-hidden group h-full flex flex-col" data-testid={`podcast-card-${ep.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={resolveImg(ep.image)}
          alt={ep.title}
          className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-transparent to-transparent" />
        <div className="absolute left-5 bottom-4 font-display text-5xl text-gold-grad leading-none">
          {ep.number}
        </div>
        <div className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-gold/95 text-midnight opacity-0 group-hover:opacity-100 transition">
          <Play className="h-5 w-5" />
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2">
          <span>{ep.tag}</span>
          <span className="text-ivory/30">·</span>
          <Clock className="h-3 w-3" /> {ep.duration}
        </div>
        <h3 className="mt-3 font-display text-2xl text-ivory">{ep.title}</h3>
        <p className="mt-3 text-sm text-ivory/65 leading-relaxed flex-1">
          {ep.description}
        </p>
        <div className="mt-5 flex items-center justify-between">
          <span className="text-xs text-ivory/50">{ep.date}</span>
          <a
            href={ep.listenUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-gold text-sm uppercase tracking-[0.2em] hover:text-saffron transition"
          >
            Listen <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </article>
  </Reveal>
);

const Podcasts = () => {
  const { content } = useContent();
  const eps = content.podcasts || [];
  return (
  <div data-testid="podcasts-page">
    <PageHero
      eyebrow="The Geomantic Kalpataru Podcast"
      title={<>Conversations on <em>ancient wisdom</em> for the modern soul.</>}
      subtitle="Episodes on geomancy, manifestation, Brahma Satya and the practical art of transformation — recorded from the studio."
      image={IMG.cosmos}
    />

    {/* Listen on platforms */}
    <section className="pt-4">
      <div className="wrap">
        <Reveal>
          <div className="glass p-6 flex flex-wrap items-center justify-between gap-6">
            <div className="text-xs uppercase tracking-[0.3em] text-ivory/55">
              Listen wherever you wander
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {[
                { Icon: Headphones, label: "Spotify", href: "https://open.spotify.com/" },
                { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/" },
                { Icon: Play, label: "Apple Podcasts", href: "https://podcasts.apple.com/" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`podcast-platform-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex items-center gap-2 border border-gold/30 px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] text-ivory/80 hover:bg-gold hover:text-midnight transition"
                >
                  <Icon className="h-3.5 w-3.5" /> {label}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Featured */}
    <section className="section pt-12">
      <div className="wrap">
        <FeaturedEpisode ep={eps[0]} />
      </div>
    </section>

    {/* All Episodes */}
    <section className="section pt-0">
      <div className="wrap">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="eyebrow mb-4">All Episodes</div>
              <h2 className="display text-4xl md:text-5xl text-ivory max-w-2xl">
                Sit with the conversations.
              </h2>
            </div>
            <p className="text-ivory/60 text-sm max-w-md">
              New episodes released bi-weekly. Subscribe on your favourite
              platform to receive each one as it lands.
            </p>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {eps.slice(1).map((ep, i) => (
            <EpisodeCard key={ep.id || i} ep={ep} i={i} />
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="section border-t border-gold/10 bg-midnight-paper/40">
      <div className="wrap text-center max-w-3xl">
        <Reveal>
          <div className="eyebrow mb-5">Be a Guest</div>
          <h2 className="display text-4xl md:text-5xl text-ivory">
            Have a story the world should <em>hear</em>?
          </h2>
          <p className="mt-6 text-ivory/70 leading-relaxed">
            We invite seekers, healers and lineage holders to share their work.
            If your path resonates, write to us.
          </p>
          <Link to="/contact" className="btn-gold mt-8" data-testid="podcasts-guest-cta">
            Propose an Episode <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-3 text-xs uppercase tracking-[0.25em] text-ivory/40">
            or write to {BRAND.email}
          </div>
        </Reveal>
      </div>
    </section>
  </div>
  );
};

export default Podcasts;
